import type { InputEnumOption } from "../ui/InputEnumOption";
import { CalculationRowType, CalculationRowUnit, Country, EmploymentListItem, EmploymentRelationSalaryPaymentMethod, EmploymentRelationTaxcard, EmploymentRelationType, IncomeEarnerType, InsuranceCompany, InternationalSocialSecurityException, Language, PensionCalculation, PensionCompany, UserDefinedRow, WorkDescription } from "../model";
import { AccountInIndex, AccountSettingsInSession, ContactAdvanced, ContactType, Employment, InsuranceContract, PensionContract, WorkerAccount  } from "../model";
import { Dates, Iban } from "../util";
import { EnumerationsLogic } from "./EnumerationsLogic";
import { Translations } from "./Translations";
import type { EmploymentSocialSecurityModel } from "./model";
import { kevaEmploymentRegCodes} from "../codegen";

/**
 * Logic related to Employment Relations.
 */
export class EmploymentLogic {

  /** Gets a new blank item as bases for UI binding. */
  public static getBlank(): Employment {
    return {
      info: {
        irIncomeEarnerTypes: [],
        salaryPaymentMethod: EmploymentRelationSalaryPaymentMethod.BankAccount,
      },
      identity: {
        language: Language.Default,
      },
      contacts: [
        {
          type: ContactType.Default,
          country: Country.Fi,
        }
      ],
      work: {
        salaryDefaults: [],
      },
      personalInfo: {
        avatar: {},
      },
      holidays: {},
      usecase: {
        data: {},
      },
      international: {
        socialSecurityExceptions: [],
        taxAtSource: {},
      },
    };
  }

  /**
   * Gets a description text for an Employment.
   * @param account The account info
   * @param type Type of employment relation
   * @param pensionCalculation Type of pension calculation.
   */
  public static getDescription(account: AccountInIndex, type: EmploymentRelationType, pensionCalculation: PensionCalculation): string {
    if (!account || (type || EmploymentRelationType.Undefined) == EmploymentRelationType.Undefined) {
      return account?.email || "-";
    }
    let descr = EnumerationsLogic.getEnumLabel("EmploymentRelationType", type);
    switch (type) {
      case EmploymentRelationType.Entrepreneur:
        if (pensionCalculation === PensionCalculation.Entrepreneur) {
          descr += " (" + Translations.get("SALAXY.ENUM.PensionCalculation.entrepreneur.label") + ")";
        } else if (pensionCalculation === PensionCalculation.PartialOwner) {
          descr += " (" + Translations.get("SALAXY.ENUM.PensionCalculation.partialOwner.label") + ")";
        } else if (pensionCalculation === PensionCalculation.SmallEntrepreneur) {
          descr += " (" + Translations.get("SALAXY.ENUM.PensionCalculation.smallEntrepreneur.label") + ")";
        } else if (pensionCalculation === PensionCalculation.Employee) {
          descr += " (" + Translations.get("SALAXY.ENUM.PensionCalculation.employee.label") + ")";
        }
        break;
      case EmploymentRelationType.Farmer:
        if (pensionCalculation === PensionCalculation.Farmer) {
          descr += " (" + Translations.get("SALAXY.ENUM.PensionCalculation.farmer.label") + ")";
        } else if (pensionCalculation === PensionCalculation.SmallFarmer) {
          descr += " (" + Translations.get("SALAXY.ENUM.PensionCalculation.smallFarmer.label") + ")";
        } else if (pensionCalculation === PensionCalculation.Employee) {
          descr += " (" + Translations.get("SALAXY.ENUM.PensionCalculation.employee.label") + ")";
        }
        break;
    }
    return descr;
  }

  /**
   * Gets a descriptive text on whether the employment is active or not.
   * @param employment Employment relation to get the text for.
   * @returns A text that describes whether the employment is active or not with the dates (start and end).
   */
  public static getActiveText(employment: Employment | EmploymentListItem): string | undefined {
    if (!employment) {
      return undefined;
    }
    // TODO: Add language versioning.
    const today = Dates.getToday(); // Set as aprameter if necessary for testing.
    const startDate = this.isListItem(employment) ? employment.data?.startDate : employment.info?.startDate;
    const endDate = this.isListItem(employment) ? employment.data?.endDate : employment.info?.endDate;
    if (!startDate && !endDate) {
      return `Aktiivinen, ei päivämääriä`;
    }
    const isActive = (!startDate || Dates.isSameOrBefore(startDate, today))
      && (!endDate || Dates.isSameOrAfter(endDate, today));
    return `${ isActive ? "Aktiiviinen" : "Ei aktiivinen" }: ${ Dates.getFormattedRange(startDate!, endDate!) }`;
  }

  /**
   * Type guard for EmploymentListItem (as opposed to Employment) .
   * @param employment An item that mey be Employment or EmploymentListItem (or undefined / null).
   * @returns True if the item is an EmploymentListItem (based on data property).
   */
  public static isListItem(employment: Employment | EmploymentListItem | undefined): employment is EmploymentListItem {
    return !!(employment as EmploymentListItem)?.data;
  }

  /**
   * Gets an AccountInIndex object based on WorkerAccount.
   * This can be used in employmetn logic methods (e.g. getDescription()) if the source is WorkerAccount and not Employment.
   */
  public static getAccountInIndex(account: WorkerAccount): AccountInIndex | undefined {
    if (!account?.avatar) {
      return undefined;
    }
    return {
      avatar: account.avatar,
      email: account.contact?.email,
      ibanNumber: account.ibanNumber,
      officialId: account.officialPersonId,
      telephone: account.contact?.telephone,
    };
  }

  /**
   * Returns options object for PensionCalculation selection by employment relation type.
   * @param employmentRelationType Employment relation type of the worker.
   */
  public static getPensionCalculationOptions(employmentRelationType: EmploymentRelationType, contracts: PensionContract[]): Array<InputEnumOption> {
    const types: PensionCalculation[] = [];
    switch (employmentRelationType as any) {
      case EmploymentRelationType.EmployedByStateEmploymentFund:
        types.push(PensionCalculation.Employee);
        break;
      case EmploymentRelationType.PerformingArtist:
        types.push(...[PensionCalculation.Employee, PensionCalculation.Compensation]);
        break;
      case EmploymentRelationType.Entrepreneur:
        types.push(...[PensionCalculation.Entrepreneur, PensionCalculation.SmallEntrepreneur, PensionCalculation.PartialOwner, PensionCalculation.Employee]);
        break;
      case EmploymentRelationType.Farmer:
        types.push(...[PensionCalculation.Farmer, PensionCalculation.SmallFarmer, PensionCalculation.Employee]);
        break;
      case EmploymentRelationType.Compensation:
        types.push(PensionCalculation.Compensation);
        break;
      case EmploymentRelationType.BoardMember:
        types.push(PensionCalculation.BoardRemuneration);
        break;
      case EmploymentRelationType.Athlete:
        types.push(PensionCalculation.Athlete);
        break;
      case EmploymentRelationType.ForeignWorker:
      case EmploymentRelationType.WorkingAbroad:
        types.push(PensionCalculation.Employee, PensionCalculation.Compensation, PensionCalculation.Entrepreneur);
        break;
      case EmploymentRelationType.HourlySalary:
      case EmploymentRelationType.MonthlySalary:
      case EmploymentRelationType.Salary:
      default:
        types.push(PensionCalculation.Employee);
        break;
    }

    // Add PublicSector if keva contract found
    if (types.some(x => x == PensionCalculation.Employee) && contracts.some((x) => x.company?.startsWith("keva"))) {
        types.push(PensionCalculation.PublicSector);
    }

    return types.map((type) => {
      const label = EnumerationsLogic.getEnumLabel("PensionCalculation", type);
      const descr = EnumerationsLogic.getEnumDescr("PensionCalculation", type);

      return { value: type, text: label, title: descr && descr !== type ? descr : undefined };
    });
  }

  /**
   * Sets the income earner types accordingly based on employment type.
   * Called when the type of employment changes
   * @param model Employment relation to update.
   */
  public static resetModelFromType(model: Employment): void {
    model.work ??=  {};
    model.info ??=  {};
    model.work.description = undefined;
    model.work.occupationCode = undefined;
    model.info.taxcard = EmploymentRelationTaxcard.Auto;
    const incomeEarnerTypes: IncomeEarnerType[] = [];

    switch (model.info.type) {
      case EmploymentRelationType.EmployedByStateEmploymentFund:
        model.info.pensionCalculation = PensionCalculation.Employee;
        incomeEarnerTypes.push(IncomeEarnerType.EmployedByStateEmploymentFund);
        break;
      case EmploymentRelationType.PerformingArtist:
        model.info.pensionCalculation = PensionCalculation.Employee;
        incomeEarnerTypes.push(IncomeEarnerType.PerformingArtist);
        break;
      case EmploymentRelationType.Entrepreneur:
        model.info.pensionCalculation = PensionCalculation.Entrepreneur;
        break;
      case EmploymentRelationType.Farmer:
        model.info.pensionCalculation = PensionCalculation.Farmer;
        break;
      case EmploymentRelationType.Compensation:
        model.info.pensionCalculation = PensionCalculation.Compensation;
        break;
      case EmploymentRelationType.BoardMember:
        model.info.pensionCalculation = PensionCalculation.BoardRemuneration;
        model.work.description = Translations.get("SALAXY.ENUM.EmploymentRelationType.boardMember.label");
        break;
      case EmploymentRelationType.Athlete:
        model.info.pensionCalculation = PensionCalculation.Athlete;
        model.work.occupationCode = "34210";
        model.work.description = Translations.get("SALAXY.ENUM.EmploymentRelationType.athlete.label");
        incomeEarnerTypes.push(IncomeEarnerType.Athlete);
        break;
      case EmploymentRelationType.ForeignWorker:
      case EmploymentRelationType.WorkingAbroad:
        model.info.pensionCalculation = PensionCalculation.Employee;
        model.info.taxcard = EmploymentRelationTaxcard.TaxAtSource;
        break;
      case EmploymentRelationType.MonthlySalary:
      case EmploymentRelationType.HourlySalary:
      case EmploymentRelationType.Salary:
      default:
        model.info.pensionCalculation = PensionCalculation.Employee;
        break;
    }
    model.info.irIncomeEarnerTypes = incomeEarnerTypes;
  }

  /**
   * Sets the income earner types accordingly based on pension calculation.
   * Called when the pension calculation changes.
   * @param model Employment relation to update.
   */
  public static resetModelFromPensionCalculation(model: Employment): void {
    const incomeEarnerTypes: IncomeEarnerType[] = model.info?.irIncomeEarnerTypes?.filter(x =>
      x != IncomeEarnerType.EntrepreneurOrFarmerNoPensionRequired && x != IncomeEarnerType.PartialOwner
    ) ?? [];
    switch (model.info?.pensionCalculation) {
      case PensionCalculation.PartialOwner:
        incomeEarnerTypes.push(IncomeEarnerType.PartialOwner);
        break;
      case PensionCalculation.SmallEntrepreneur:
      case PensionCalculation.SmallFarmer:
        incomeEarnerTypes.push(IncomeEarnerType.EntrepreneurOrFarmerNoPensionRequired);
        break;
    }
    model.info ??= {};
    model.info.irIncomeEarnerTypes = incomeEarnerTypes;
  }

  /**
   * Sets the salary default rows (rows that are used when creating new claculations)
   * based on the employment type.
   * @param type Employment type
   * @param work Work description that contains the salary defaults (will be reset).
   */
  public static setSalaryDefaultRows(type: EmploymentRelationType, work: WorkDescription): void {
    work.salaryDefaults = [];
    const defaultRow: UserDefinedRow = {
      rowIndex: 0,
      rowType: null,
      count: null,
      price: null,
      message: null,
    };

    defaultRow.message = null;
    switch (type) {
      case EmploymentRelationType.MonthlySalary:
        defaultRow.rowType = CalculationRowType.MonthlySalary;
        defaultRow.count = 1;
        defaultRow.price = 0;
        defaultRow.unit = null;
        break;
      case EmploymentRelationType.HourlySalary:
        defaultRow.rowType = CalculationRowType.HourlySalary;
        defaultRow.count = 1;
        defaultRow.price = 0;
        defaultRow.unit = CalculationRowUnit.Hours;
        break;
      case EmploymentRelationType.Salary:
        defaultRow.rowType = CalculationRowType.Salary;
        defaultRow.count = 1;
        defaultRow.price = 0;
        defaultRow.unit = null;
        break;
      case EmploymentRelationType.EmployedByStateEmploymentFund:
        defaultRow.rowType = CalculationRowType.HourlySalary;
        defaultRow.count = 1;
        defaultRow.price = 0;
        defaultRow.unit = CalculationRowUnit.Hours;
        break;
      case EmploymentRelationType.PerformingArtist:
        defaultRow.rowType = CalculationRowType.Salary;
        defaultRow.count = 1;
        defaultRow.price = 0;
        defaultRow.unit = null;
        break;
      case EmploymentRelationType.Entrepreneur:
      case EmploymentRelationType.Farmer:
        break;
      case EmploymentRelationType.Compensation:
        defaultRow.rowType = CalculationRowType.Compensation;
        defaultRow.count = 1;
        defaultRow.price = 0;
        defaultRow.unit = null;
        break;
      case EmploymentRelationType.BoardMember:
        defaultRow.rowType = CalculationRowType.Board;
        defaultRow.message = Translations.get("SALAXY.ENUM.CalculationRowType.board.label");
        defaultRow.count = 1;
        defaultRow.price = 0;
        defaultRow.unit = null;
        break;
      case EmploymentRelationType.Athlete:
        defaultRow.rowType = CalculationRowType.Salary;
        defaultRow.message = Translations.get("SALAXY.ENUM.EmploymentRelationType.athlete.salaryRowMessage");
        defaultRow.count = 1;
        defaultRow.price = 0;
        defaultRow.unit = null;
        break;
    }
    if (defaultRow.rowType != null) {
      work.salaryDefaults.push(defaultRow);
    }
  }

  /**
   * Gets the social security pensions model (available options) for the current selections.
   * @param settings Account settings in the session
   * @param type Type of employment relation selected
   * @param socialSecurity Whether the person is in Finnish or foregion social security - or mixed
   * @param pensionCalculation Type of Pension calculation: employee/TyEL or others (YEL, farmer etc.)
   * @param pensionCompany Pension company (if several) that is selected.
   * @param insuranceCompany Insurance company (if several) that is selected.
   * @param socSecExceptions The selected social security exceptions (not subject to)
   */
  public static getSocialSecurityModel(
    settings: AccountSettingsInSession,
    type: EmploymentRelationType,
    socialSecurity: "default" | "foreign" | "mixed",
    pensionCalculation: PensionCalculation,
    pensionCompany: PensionCompany,
    insuranceCompany: InsuranceCompany,
    socSecExceptions: InternationalSocialSecurityException[],
    ): EmploymentSocialSecurityModel | undefined {
    const insuranceContractNumbers = this.getValidContractNumbers(insuranceCompany, settings.insuranceContracts ?? [])
      .map((num) => ({ value: num, text: num }));
    let pensionCompanies: InputEnumOption[] = this.getValidCompanies(settings.pensionContracts ?? []).map((company) => {
      const label = EnumerationsLogic.getEnumLabel("PensionCompany", company);
      const descr = EnumerationsLogic.getEnumDescr("PensionCompany", company);
      return { value: company, text: label, title: descr && descr !== company ? descr : undefined };
    });

    // filter in/out keva companies
    pensionCompanies = pensionCalculation == PensionCalculation.PublicSector ? pensionCompanies.filter(x => x.value.startsWith("keva")) : pensionCompanies.filter(x => !x.value.startsWith("keva"));

    const pensionContractNumbers: InputEnumOption[] = this.getValidContractNumbers(pensionCompany, settings.pensionContracts ?? [])
      .map((num) => ({ value: num, text: num }));
    const insuranceCompanies: InputEnumOption[] = this.getValidCompanies(settings.insuranceContracts ?? []).map((company) => {
      const label = EnumerationsLogic.getEnumLabel("InsuranceCompany", company);
      const descr = EnumerationsLogic.getEnumDescr("InsuranceCompany", company);
      return { value: company, text: label, title: descr && descr !== company ? descr : undefined };
    });

    // TODO: Why are these based on getValidContracts() and not getValidCompanies()? We can perhaps get rid of separate showPensionCompanySelection etc.
    const showSocialSecurity = (type != EmploymentRelationType.ForeignWorker || socialSecurity != "foreign");
    const pensionCalculationOptions = (showSocialSecurity && socSecExceptions.indexOf(InternationalSocialSecurityException.NotSubjectToEarningsRelatedPensionInsurance) < 0)
      ? EmploymentLogic.getPensionCalculationOptions(type, settings.pensionContracts ?? []) : null;
    const showPension = (pensionCalculationOptions && ( pensionCalculation == PensionCalculation.Employee || pensionCalculation == PensionCalculation.PublicSector)) ?? false;
    const showPensionCompanySelection = showPension && this.getValidContracts(settings.pensionContracts ?? []).length > 1;
    const showInsurance = showSocialSecurity && socSecExceptions.indexOf(InternationalSocialSecurityException.NotSubjectToAccidentAndOccupationalDiseaseInsurance) < 0;
    const showInsuranceCompanySelection = showInsurance && this.getValidContracts(settings.insuranceContracts ?? []).length > 1;

    const insertNotSelected = (options: InputEnumOption[]): InputEnumOption[] => {
      return [{ value: "" as any, text: Translations.get("SALAXY.UI_Terms.notChosen"), title: undefined }, ...options];
    };

    return {
      pensionCalculationOptions,
      showPension,
      pensionCompanies: insertNotSelected(pensionCompanies),
      pensionContractNumbers: (showPension && pensionContractNumbers.length > 1) ? insertNotSelected(pensionContractNumbers) : null,
      showPensionCompanySelection,
      showInsurance,
      insuranceCompanies: insertNotSelected(insuranceCompanies),
      insuranceContractNumbers: (showInsurance && insuranceContractNumbers.length > 1) ? insertNotSelected(insuranceContractNumbers) : null,
      showInsuranceCompanySelection,
      isInternational: type == EmploymentRelationType.ForeignWorker || type == EmploymentRelationType.WorkingAbroad,
      showSocialSecurity,
      employmentRegCodes: insertNotSelected(this.getEmploymentRegCodeOptions(pensionCompany)),
    };
  }

  /**
   * Gets the companies that are valid for today (insurance or pension companies).
   * @param contracts Collection of contracts
   * @returns Those companies (id's) that have contracts that are valid today.
   */
  public static getValidCompanies(contracts: (PensionContract | InsuranceContract)[]): string[] {
    const validContracts = this.getValidContracts(contracts);
    const companies: string[] = [];
    validContracts.forEach((x) => {
      if (x.company && companies.indexOf(x.company) === -1) {
        companies.push(x.company);
      }
    });
    return companies;
  }

  /**
   * Gets the contract numbers (for a sepcific insurance company) that are valid for today.
   * @param company Insurance company for which the numers are fetched.
   * @param contracts Collection of contracts
   * @returns Those contract numbers that are valid for today (for the given company).
   */
  public static getValidContractNumbers(company: string, contracts: (PensionContract | InsuranceContract)[]): string[] {
    if (!company) {
      return [];
    }
    return this.getValidContracts(contracts)
      .filter((x) => x.company == company)
      .map((x) => x.contractNumber)
      .filter((x): x is string => x != null);
  }

  /**
   * Gets the contracts that are valid for today.
   * @param contracts Collection of contracts
   * @returns Those contracts that are valid for today.
   */
  public static getValidContracts(contracts: (PensionContract | InsuranceContract)[]): (PensionContract | InsuranceContract)[] {
    const forDate = Dates.getToday();
    // check dates
    const validContracts = (contracts ?? []).filter((x) =>
      (!x.startDate || Dates.isSameOrBefore(x.startDate, forDate)) &&
      (!x.endDate || Dates.isSameOrAfter(x.endDate, forDate))
    );
    return validContracts;
  }

  /**
   * Gets a contact of specific type.
   * @param model Employment relation to get the contact from.
   * @param type Type of contact to get. Use null to fetch any contact type (creates Default if requested).
   * @param createIfNotExists If true creates the contact of given type if one does not exist.
   * @returns Contact of given type or undefined if one is not found AND createIfNotExists is false.
   */
  public static getContact(model: Employment, type: ContactType | null, createIfNotExists = true): ContactAdvanced | undefined {
    if (!model) {
      return undefined;
    }
    if (!model.contacts) {
      model.contacts = [];
    }
    let result = model.contacts.find(x => !type || x.type == type);
    if (!result && createIfNotExists) {
      type = type || ContactType.Default;
      result = {
        type,
        country: type == ContactType.Default ? Country.Fi : null,
      };
      model.contacts.push(result);
    }
    return result;
  }

  /**
   * Populates given user with test data.
   * Sets values only if the firstName and lastName have been given.
   * @param employment - Employment relation to populate with test data.
   * @param useRealPersonaIdRange - If true, uses the real personal id range instead of artificial ID range ("keinohetu": last 3 numbers start with 9).
   */
  public static populateWithTestData(employment: Employment, useRealPersonaIdRange = false): void {
    if (!employment?.identity?.firstName || !employment.identity.lastName) {
      return;
    }
    const contact = this.getContact(employment, ContactType.Default, true) ?? {};
    contact.email = this.cleanupEmailPart(employment.identity.firstName) + "." + this.cleanupEmailPart(employment.identity.lastName) + "@palkkaus.fi";
    contact.telephone = "040 " + (1000000 + Math.round((Math.random() * 8999999))).toString();
    // hetu
    const birthYear = (new Date().getFullYear()) - (20 + Math.round(Math.random() * 40));
    const birthDate = new Date(new Date(birthYear, 0, 1).getTime() + Math.round(Math.random() * (new Date(birthYear + 1, 11, 31).getTime() - new Date(birthYear, 0, 1).getTime())));
    const separator = birthYear >= 2000 ? "A" : "-";
    const personNumber = useRealPersonaIdRange ? Math.round(Math.random() * 899) : (900 + Math.round(Math.random() * 99));
    const checksumLetters = "0123456789ABCDEFHJKLMNPRSTUVWXY";
    const sDate = Dates.format(birthDate, "DDMMYY");
    const idChecksum = checksumLetters[parseInt(sDate + personNumber.toString(), 10) % 31];
    employment.identity.personalId = sDate + separator + personNumber.toString() + idChecksum;
    employment.info ??= {};
    employment.info.ibanNumber = Iban.getDummyIban();
    if (!useRealPersonaIdRange) {
      contact.city = "PALKKALA";
      contact.postalCode = "01337";
      contact.street = "Ansionkatu 1";
    }
  }

  /**
   * Returns true if the personal ID is other identifier than Finnish Personal Identification Number (keinohetu).
   * @param ssn Personal ID to check.
   */
  public static isPersonalIdOther(ssn: string): boolean {
    /** 8th character is 9, this will change in 2024 */
    const regex = /^.{7}[9]/;
    return regex.test((ssn || "").trim());
  }

  /**
   * Returns valid employment reg codes for the selected pensions company.
   * @param pensionCompany - Keva pension company.
   * @returns Options for the employment reg codes.
   */
  public static getEmploymentRegCodeOptions(pensionCompany: PensionCompany): Array<InputEnumOption> {
    return kevaEmploymentRegCodes.filter((x) => x.pensionCompany === pensionCompany).map((regCode) =>
     ({ value: regCode.code, text: Translations.getLanguage() === Language.Sv ? regCode.labelSv : regCode.labelFi }));
  }

  private static cleanupEmailPart(name: string): string {
    if (!name) {
      return name;
    }
    // to lower case
    name = name.toLowerCase();
    // remove accents
    name = name.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    // leave only letters
    name = name.replace(/[^a-z]+/g, "");
    return name;
  }

}